import { Component, OnInit, OnChanges, OnDestroy, SimpleChanges, Input, signal, inject, computed } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../../core/service/api-service/api-service';
import { API } from '../../../core/API';

@Component({
  selector: 'app-documents-list',
  standalone: false,
  templateUrl: './documents-list.component.html',
  styleUrls: ['./documents-list.component.css']
})
export class DocumentsListComponent implements OnInit, OnChanges, OnDestroy {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  private destroy$ = new Subject<void>();

  @Input() pageCode: string | null = null;

  isStandalonePage = false;
  pageTitle = '';

  documents = signal<any[]>([]);
  isLoading = signal(true);
  errorMessage = signal<string | null>(null);
  selectedPageCode = signal<string>('');

  showForm = signal(false);
  editingDocument = signal<any>(null);
  isSaving = signal(false);
  formData = signal({ title: '', order: 1, fileId: null as number | null });
  selectedFile = signal<File | null>(null);

  pendingDelete = signal<number | null>(null);

  pagesLoading = signal(true);
  pagesData = signal<any[]>([]);
  pageCodes = computed(() =>
    this.pagesData()
      .filter((page: any) => page.has_documents === 1 || page.has_documents === '1')
      .map((page: any) => page.page_code)
  );

  ngOnInit(): void {
    const routeData = this.route.snapshot.data;
    if (routeData['pageCode']) {
      this.pageCode = routeData['pageCode'];
      this.pageTitle = routeData['pageTitle'] ?? '';
      this.isStandalonePage = true;
    }
    this.loadPages();
    if (this.pageCode) {
      this.selectedPageCode.set(this.pageCode);
      this.loadDocuments();
    }
  }

  loadPages(): void {
    this.pagesLoading.set(true);
    this.api.get(API.PAGES.GET_ALL)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const flattenPages = (pages: any[]): any[] => {
            let result: any[] = [];
            pages.forEach((page: any) => {
              result.push(page);
              if (page.children && page.children.length > 0) {
                result = result.concat(flattenPages(page.children));
              }
            });
            return result;
          };
          this.pagesData.set(flattenPages(response.data || []));
          this.pagesLoading.set(false);
        },
        error: () => {
          this.pagesLoading.set(false);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageCode'] && !changes['pageCode'].firstChange && this.pageCode) {
      this.selectedPageCode.set(this.pageCode);
      this.loadDocuments();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadDocuments(): void {
    if (!this.selectedPageCode()) return;
    this.isLoading.set(true);
    this.api.get(`${API.DOCUMENTS.GET_BY_PAGE}/${this.selectedPageCode()}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.documents.set(response.data || []);
          this.isLoading.set(false);
        },
        error: () => {
          this.errorMessage.set('Erreur lors du chargement des documents');
          this.isLoading.set(false);
        }
      });
  }

  openAddForm(): void {
    this.editingDocument.set(null);
    this.formData.set({ title: '', order: this.documents().length + 1, fileId: null });
    this.selectedFile.set(null);
    this.errorMessage.set(null);
    this.showForm.set(true);
  }

  openEditForm(doc: any): void {
    this.editingDocument.set(doc);
    this.formData.set({
      title: doc.public_document_first_title || '',
      order: doc.public_document_display_order || 1,
      fileId: doc.public_document_file_id || null
    });
    this.selectedFile.set(null);
    this.errorMessage.set(null);
    this.showForm.set(true);
  }

  closeForm(): void {
    this.showForm.set(false);
    this.editingDocument.set(null);
    this.selectedFile.set(null);
    this.errorMessage.set(null);
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile.set(input.files?.[0] ?? null);
  }

  updateField(field: string, value: any): void {
    this.formData.set({ ...this.formData(), [field]: value });
  }

  onSave(): void {
    if (!this.formData().title.trim()) {
      this.errorMessage.set('Le titre est requis');
      return;
    }
    this.isSaving.set(true);
    this.errorMessage.set(null);

    const file = this.selectedFile();
    const editing = this.editingDocument();

    let body: any;
    if (file) {
      body = new FormData();
      body.append('public_document_first_title', this.formData().title);
      body.append('public_document_display_order', String(this.formData().order));
      body.append('page_code', this.selectedPageCode());
      body.append('file', file);
      if (editing && this.formData().fileId) {
        body.append('public_document_file_id', String(this.formData().fileId));
      }
    } else {
      body = {
        public_document_first_title: this.formData().title,
        public_document_display_order: this.formData().order,
        page_code: this.selectedPageCode(),
        public_document_file_id: this.formData().fileId
      };
    }

    const request = editing
      ? this.api.put(`${API.DOCUMENTS.UPDATE}/${editing.public_document_id}`, body)
      : this.api.post(API.DOCUMENTS.ADD, body);

    request.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { this.isSaving.set(false); this.closeForm(); this.loadDocuments(); },
      error: () => { this.errorMessage.set('Erreur lors de l\'enregistrement'); this.isSaving.set(false); }
    });
  }

  confirmDelete(id: number): void { this.pendingDelete.set(id); }
  cancelDelete(): void { this.pendingDelete.set(null); }

  applyDelete(): void {
    const id = this.pendingDelete();
    if (!id) return;
    this.pendingDelete.set(null);
    this.api.delete(`${API.DOCUMENTS.DELETE}/${id}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadDocuments(),
        error: () => this.errorMessage.set('Erreur lors de la suppression')
      });
  }
}
